import { NestedMembership, FlatMembership } from '../types/membership.types';

/**
 * Flattens nested membership hierarchy into a flat array with tier levels
 */
export const flattenMemberships = (
  nestedMemberships: NestedMembership[]
): FlatMembership[] => {
  const flattened: FlatMembership[] = [];

  const traverse = (
    membership: NestedMembership,
    level: number,
    path: number[]
  ) => {
    const currentPath = [...path, membership.membership_id];
    
    flattened.push({
      membership_id: membership.membership_id,
      local_id: membership.local_id,
      membership_name: membership.membership_name,
      membership_description: membership.membership_description,
      discount_percentage: membership.discount_percentage,
      is_default: membership.is_default,
      created_at: membership.created_at,
      next_membership_id: membership.next_membership_id,
      next_level_name: membership.next_membership?.membership_name || null,
      next_level_required_spent: membership.next_level_required_spent,
      client_count: membership.client_count,
      tier_level: level,
      has_clients: membership.client_count > 0,
      can_delete: membership.client_count === 0,
      full_path: currentPath,
    });

    if (membership.next_membership) {
      traverse(membership.next_membership, level + 1, currentPath);
    }
  };

  nestedMemberships.forEach((membership) => {
    traverse(membership, 1, []);
  });

  return flattened;
};

/**
 * Checks if setting a membership as next tier would create a circular reference
 */
export const wouldCreateCircularReference = (
  currentMembershipId: number,
  potentialNextId: number,
  allMemberships: FlatMembership[]
): boolean => {
  if (currentMembershipId === potentialNextId) {
    return true; // Self-reference
  }

  const visited = new Set<number>([currentMembershipId]);
  let currentId: number | null = potentialNextId;

  while (currentId) {
    if (visited.has(currentId)) {
      return true; // Circular reference detected
    }
    visited.add(currentId);
    const searchId: number = currentId;
    const nextMembership: FlatMembership | undefined = allMemberships.find(m => m.membership_id === searchId);
    currentId = nextMembership?.next_membership_id || null;
  }

  return false;
};

/**
 * Gets all memberships that can be selected as next tier for a given membership
 */
export const getAvailableNextTiers = (
  currentMembershipId: number | null,
  allMemberships: FlatMembership[]
): FlatMembership[] => {
  return allMemberships.filter(m => {
    // Exclude current membership
    if (currentMembershipId && m.membership_id === currentMembershipId) {
      return false;
    }

    // Exclude if it would create a circular reference
    if (currentMembershipId && wouldCreateCircularReference(currentMembershipId, m.membership_id, allMemberships)) {
      return false;
    }

    return true;
  });
};

/**
 * Finds the current default membership in the list
 */
export const findDefaultMembership = (
  memberships: FlatMembership[]
): FlatMembership | null => {
  return memberships.find(m => m.is_default) || null;
};

/**
 * Formats currency for display (Chilean pesos)
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
  }).format(amount);
};

/**
 * Gets the hierarchical depth of a membership
 */
export const getMembershipDepth = (membership: NestedMembership): number => {
  let depth = 1;
  let current = membership.next_membership;
  
  while (current) {
    depth++;
    current = current.next_membership;
  }
  
  return depth;
};
